// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import { RootState } from 'admin/core/store';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import * as storageActions from 'admin/storage/actions';
import * as computeResourceActions from 'admin/computeResource/actions';
import { connect } from 'react-redux';
import {
    RouteComponentProps,
    withRouter,
} from 'react-router';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import { Loader } from 'common/components';
import { WithErrorPages } from 'common/components/WithPageNotFound/WithErrorPages';
import {
    Action,
    Button,
    Icon,
    Item,
    Translate,
} from '@plesk/ui-library';
import { Link } from 'react-router-dom';
import { pathTo } from 'common/helpers/core';
import { formatSizeFromGiB } from 'common/helpers/units';
import PageHeader from 'common/components/PageHeader/PageHeader';
import {
    PageSection,
    SubTitleRow,
    SubTitleSection,
    SubTitleSectionLabel,
} from 'common/components/PageHeader/Styles';
import {
    ICONS,
    SIZE,
} from 'common/constants';
import {
    STORAGE_TYPES_TRANSLATION_MAP,
    StorageType,
} from 'common/api/resources/StorageType';
import styled from 'styled-components';
import { Dialog } from 'common/components/Dialog/Dialog';
import StorageForm from 'admin/storage/containers/StorageForm';
import ComputeResourceTable from 'admin/computeResource/list/containers/ComputeResourceTable/ComputeResourceTable';
import { StyledTable } from 'common/components/styles/StyledTable';
import { useComputeResourceDialogs } from 'admin/computeResource/hooks/useComputeResourceDialogs';
import TaskDialog from 'admin/task/containers/TaskDialog';
import { setTask } from 'admin/task/actions';
import {
    ITaskResponse,
    tasks as tasksApi,
    TASK_STATUS,
} from 'common/api/resources/Task';
import { COMPUTE_RESOURCE_STATUS } from 'admin/computeResource/constants';
import { ComputeResourceVmStatus } from 'common/api/resources/ComputeResourceVm/model';
import StatusesList from 'common/components/StatusesList/StatusesList';
import { StatusType } from 'common/components/Status/Status';
import CardHeader from 'admin/dashboard/components/CardHeader';
import InfiniteScroll from 'common/components/InfinityScroll/InfinityScroll';
import { useIsFirstLoading } from 'common/hooks/useIsFirstLoading';

export type StoragePageProps =
    RouteComponentProps<{ id: string }> &
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

const StatsCardsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-bottom: 24px;
`;

export const StoragePage: React.FC<StoragePageProps> = ({
    storage,
    isLoading,
    computeResourceList,
    isLoadingComputeResources,
    storageActions: {
        getStorage,
    },
    computeResourceActions: {
        getComputeResources,
        loadComputeResourcesOnScroll,
        resetComputeResourceList,
    },
    setTaskItem,
    history,
    match: { params },
}) => {
    const [isFormOpen, setIsFormOpen] = React.useState(false);
    const [isTaskDialogOpen, setIsTaskDialogOpen] = React.useState(false);
    // Latest failed task per CR id. Pre-fetched alongside the CR list so the
    // Details click is instant (mirrors the tasks-list flow — no fetch on click).
    const [latestFailedTasks, setLatestFailedTasks] = React.useState<Record<number, ITaskResponse>>({});

    const storageId = parseInt(params.id, 10);

    // Clear CRs left in the shared slice by another page before our filtered fetch.
    React.useLayoutEffect(() => {
        resetComputeResourceList();
    }, [storageId, resetComputeResourceList]);

    const computeResources = computeResourceList.data;
    const isFirstLoadingComputeResources = useIsFirstLoading(isLoadingComputeResources);

    const fetchComputeResources = React.useCallback(
        async (): Promise<void> => {
            await getComputeResources({ filters: { storage: storageId } });
        },
        [getComputeResources, storageId]
    );

    // After detaching a CR from this storage the row is already removed from the
    // list slice by the action; refresh the storage item so the count card updates.
    const handleDetached = React.useCallback(
        async (): Promise<void> => {
            await getStorage(storageId);
        },
        [getStorage, storageId]
    );

    const onMount = async () => {
        await Promise.all([getStorage(storageId), fetchComputeResources()]);
    };

    const handleEditOpen = () => {
        setIsFormOpen(true);
    };

    const handleEditClose = () => {
        setIsFormOpen(false);
    };

    const crDialogs = useComputeResourceDialogs({
        items: computeResources,
        onChanged: fetchComputeResources,
    });

    // Pre-fetch latest failed task for each failed CR — fire as soon as the
    // CR list is known. Click on the Details icon then just opens the dialog
    // with already-loaded data (no request in flight).
    const failedCrIdsKey = React.useMemo(
        () => computeResources
            .filter(cr => cr.status === COMPUTE_RESOURCE_STATUS.FAILED)
            .map(cr => cr.id)
            .join(','),
        [computeResources]
    );
    React.useEffect(() => {
        if (failedCrIdsKey === '') {
            setLatestFailedTasks({});
            return;
        }
        let cancelled = false;
        const ids = failedCrIdsKey.split(',').map(Number);
        Promise.all(ids.map(id => tasksApi.list({
            filters: { compute_resource_id: id, status: TASK_STATUS.FAILED },
            page: 1,
        })))
            .then(responses => {
                if (cancelled) {
                    return;
                }
                const map: Record<number, ITaskResponse> = {};
                responses.forEach((res, idx) => {
                    if (res.data.data.length > 0) {
                        map[ids[idx]] = res.data.data[0];
                    }
                });
                setLatestFailedTasks(map);
            })
            .catch(() => {
                // Network/API error — leave latestFailedTasks empty; the
                // Details icon will simply no-op (handleCrStatusDetails
                // already early-returns when the task is missing).
            });
        return () => {
            cancelled = true;
        };
    }, [failedCrIdsKey]);

    const handleCrStatusDetails = (id: number) => {
        const task = latestFailedTasks[id];
        if (!task) {
            return;
        }
        setTaskItem(task);
        setIsTaskDialogOpen(true);
    };

    const handleTaskDialogClose = () => setIsTaskDialogOpen(false);

    const freeSpace = storage.free_space ?? 0;

    const crCounts = storage.compute_resources_status_counts;
    const vsCounts = storage.servers_status_counts;
    const fdCounts = storage.failover_domains_status_counts;
    const isEditDisabled = storage.compute_resources_count > 0
        || storage.servers_count > 0
        || (storage.failover_domains?.length ?? 0) > 0;

    const renderTitle = () => (
        <PageSection>
            <Action
                component={Link}
                to={pathTo('storage')}
            >
                <Translate content="storage.page.breadcrumb" />
            </Action>
            <Icon name={ICONS.CHEVRON_RIGHT} />
            {storage.name}
        </PageSection>
    );

    const renderSubtitle = () => (
        <SubTitleRow>
            <SubTitleSection>
                <SubTitleSectionLabel>
                    <Translate content="storage.page.type" />
                </SubTitleSectionLabel>
                {STORAGE_TYPES_TRANSLATION_MAP[storage.type.name]}
            </SubTitleSection>
            {storage.storage_tag && (
                <SubTitleSection>
                    <SubTitleSectionLabel>
                        <Translate content="storage.page.storageTier" />
                    </SubTitleSectionLabel>
                    {storage.storage_tag.name}
                </SubTitleSection>
            )}
        </SubTitleRow>
    );

    return (
        <WithErrorPages onMount={onMount} deps={[storageId]}>
            <Loader isLoading={isLoading}>
                <PageHeader
                    title={renderTitle()}
                    subtitle={renderSubtitle()}
                    actionButton={(
                        <Button
                            ghost={true}
                            icon={ICONS.PENCIL}
                            onClick={handleEditOpen}
                            disabled={isEditDisabled}
                            tooltip={
                                <Translate content={
                                    isEditDisabled
                                        ? 'storage.tooltip.editDisabled'
                                        : 'storage.tooltip.edit'
                                } />
                            }
                        />
                    )}
                />
                <StatsCardsGrid>
                    <Item
                        view="card"
                        title={
                            <CardHeader>
                                <Translate content="storage.page.stats.freeSpace" />: {formatSizeFromGiB(freeSpace)}
                            </CardHeader>
                        }
                    />

                    <Item
                        view="card"
                        title={
                            <CardHeader>
                                <Translate content="storage.page.stats.computeResources" />: {storage.compute_resources_count}
                            </CardHeader>
                        }
                    >
                        {crCounts && (
                            <StatusesList
                                items={[
                                    {
                                        status: StatusType.SUCCESS,
                                        labelKey: 'storage.page.stats.active',
                                        value: crCounts.active,
                                    },
                                    {
                                        status: StatusType.FAILED,
                                        labelKey: 'storage.page.stats.failed',
                                        value: crCounts.failed,
                                    },
                                    {
                                        status: StatusType.UNAVAILABLE,
                                        labelKey: 'storage.page.stats.unavailable',
                                        value: crCounts.unavailable,
                                    },
                                ]}
                            />
                        )}
                    </Item>

                    <Item
                        view="card"
                        title={
                            <CardHeader to={pathTo(`storage/${storageId}/servers`)}>
                                <Translate content="storage.page.stats.virtualServers" />: {storage.servers_count}
                            </CardHeader>
                        }
                    >
                        {vsCounts && (
                            <StatusesList
                                items={[
                                    {
                                        status: StatusType.SUCCESS,
                                        labelKey: 'storage.page.stats.running',
                                        value: vsCounts.started,
                                        to: pathTo(`storage/${storageId}/servers?status=${ComputeResourceVmStatus.STARTED}`),
                                    },
                                    {
                                        status: StatusType.STOPPED,
                                        labelKey: 'storage.page.stats.stopped',
                                        value: vsCounts.stopped,
                                        to: pathTo(`storage/${storageId}/servers?status=${ComputeResourceVmStatus.STOPPED}`),
                                    },
                                    {
                                        status: StatusType.PAUSED,
                                        labelKey: 'storage.page.stats.paused',
                                        value: vsCounts.paused,
                                        to: pathTo(`storage/${storageId}/servers?status=${ComputeResourceVmStatus.PAUSED}`),
                                    },
                                    {
                                        status: StatusType.UNAVAILABLE,
                                        labelKey: 'storage.page.stats.unavailable',
                                        value: vsCounts.unavailable,
                                        to: pathTo(`storage/${storageId}/servers?status=${ComputeResourceVmStatus.UNAVAILABLE}`),
                                    },
                                ]}
                            />
                        )}
                    </Item>

                    <Item
                        view="card"
                        title={
                            <CardHeader to={pathTo(`storage/${storageId}/failover_domains`)}>
                                <Translate content="storage.page.stats.failoverDomains" />: {storage.failover_domains?.length ?? 0}
                            </CardHeader>
                        }
                    >
                        {fdCounts && (
                            <StatusesList
                                items={[
                                    {
                                        status: StatusType.SUCCESS,
                                        labelKey: 'storage.page.stats.active',
                                        value: fdCounts.active,
                                        to: pathTo(`storage/${storageId}/failover_domains?is_active=1`),
                                    },
                                    {
                                        status: StatusType.PAUSED,
                                        labelKey: 'storage.page.stats.notActive',
                                        value: fdCounts.inactive,
                                        to: pathTo(`storage/${storageId}/failover_domains?is_active=0`),
                                    },
                                ]}
                            />
                        )}
                    </Item>
                </StatsCardsGrid>

                <StyledTable>
                    <InfiniteScroll
                        loadMore={loadComputeResourcesOnScroll}
                        hasMore={!!computeResourceList.links.next}
                    >
                        <Loader isLoading={isFirstLoadingComputeResources}>
                            <ComputeResourceTable
                                items={computeResources}
                                onCreate={crDialogs.handleCreate}
                                onEdit={crDialogs.handleEdit}
                                onRemove={crDialogs.handleRemove}
                                onSettingsClick={crDialogs.handleSettings}
                                onDetailsClick={handleCrStatusDetails}
                                storageId={storageId}
                                isDetachStorageDisabled={storage.type.name === StorageType.VZ}
                                onDetached={handleDetached}
                            />
                        </Loader>
                    </InfiniteScroll>
                </StyledTable>
            </Loader>

            <Dialog
                heading={<Translate content="storage.form.titleEdit" />}
                closeHandler={handleEditClose}
                isOpen={isFormOpen}
                size={SIZE.XS}
            >
                <StorageForm closeDialog={handleEditClose} keepItem={true} />
            </Dialog>
            {crDialogs.dialogs}
            <TaskDialog handleClose={handleTaskDialogClose} isOpen={isTaskDialogOpen} />
        </WithErrorPages>
    );
};

const mapStateToProps = (state: RootState) => ({
    storage: state.storage.item,
    isLoading: state.app.loadingFlags.has(LOADING_FLAGS.STORAGE_GET),
    computeResourceList: state.computeResource.list,
    isLoadingComputeResources: state.app.loadingFlags.has(LOADING_FLAGS.COMPUTE_RESOURCE_LIST),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    storageActions: bindActionCreators(storageActions, dispatch),
    computeResourceActions: bindActionCreators(computeResourceActions, dispatch),
    setTaskItem: bindActionCreators(setTask, dispatch),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(StoragePage));
